import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, ButtonProps, Checkbox, ExpandableSection, FormGroup, HelperText, HelperTextItem, Modal, ModalVariant, Stack, StackItem, Tab, Tabs, TabTitleText, TextArea } from '@patternfly/react-core';
import { CheckCircleIcon, TimesCircleIcon } from '@patternfly/react-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { appConfigSelectors } from '../store/appConfig/appConfigSlice';
import { DisabledFeature, isFeatureDisabled } from '../store/appConfig/appDeploymentInfoTypes';
import {
    assistantSelectors,
    fetchRoiModelsStarted,
    generateRoiOperationStarted,
    validateRoiOperationsStarted,
    resetAssistantState,
} from '../store/assistant/assistantSlice';
import { contouringSelectors } from '../store/contouring/contouringSlice';
import { StoreState } from '../store/store';

import './roi-operation-modal.css';

/** Set to true to hide the "continue previous thread" UI and disable thread continuity in API calls. */
const DISABLE_CONTINUE_THREAD = false;

type TabKey = 'operation' | 'assistant' | 'validate';

interface RoiOperationModalProps {
    isOpen: boolean,
    onConfirm: (text: string) => void,
    onClose: () => void,
    variant?: ModalVariant,
    confirmLabel?: string,
    cancelLabel?: string,
    confirmVariant?: ButtonProps['variant'];
    operation?: string,
    customizationOutputId?: string,
    roiName?: string,
}

const RoiOperationModal = (props: RoiOperationModalProps) => {
    const { isOpen, onClose, onConfirm, confirmVariant, operation, customizationOutputId } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const deploymentInfo = useSelector(appConfigSelectors.selectAppDeploymentInfo);
    const showAssistantTab = deploymentInfo ? !isFeatureDisabled(deploymentInfo, DisabledFeature.RoiOperationAssistant) : false;
    const showValidateTab = deploymentInfo ? !isFeatureDisabled(deploymentInfo, DisabledFeature.RoiOperationValidator) : false;

    // Redux assistant state
    const roiModels = useSelector(assistantSelectors.selectRoiModels);
    const areRoiModelsLoading = useSelector(assistantSelectors.selectAreRoiModelsLoading);
    const roiModelsError = useSelector(assistantSelectors.selectRoiModelsError);
    const isRoiOpGenerating = useSelector(assistantSelectors.selectIsRoiOpGenerating);
    const roiOpError = useSelector(assistantSelectors.selectRoiOpError);
    const roiOpResult = useSelector(assistantSelectors.selectRoiOpResult);
    const roiOpThreadId = useSelector(assistantSelectors.selectRoiOpThreadId);
    const isRoiValidating = useSelector(assistantSelectors.selectIsRoiValidating);
    const roiValidateError = useSelector(assistantSelectors.selectRoiValidateError);
    const roiValidateResults = useSelector(assistantSelectors.selectRoiValidateResults);

    const currentModelInfo = useSelector((state: StoreState) =>
        customizationOutputId ? contouringSelectors.selectModelInfoForCustomizationOutput(state, customizationOutputId) : undefined
    );
    const currentModelName = currentModelInfo?.modelName;
    const currentModelLabel = currentModelInfo?.label;

    // Auto-detect model from current customization against API-returned models
    const detectedModel = useMemo(() => {
        if (!currentModelName || !roiModels) { return undefined; }
        return roiModels.includes(currentModelName) ? currentModelName : null;
        // undefined = still loading/unknown, null = model not supported
    }, [currentModelName, roiModels]);

    const modelDisplayName = currentModelLabel && currentModelName
        ? `${currentModelLabel} (${currentModelName})`
        : currentModelName;

    const modelError = useMemo(() => {
        if (roiModelsError) { return roiModelsError; }
        if (detectedModel === null) {
            return t('assistant.roiOp.modelNotSupported', { model: modelDisplayName });
        }
        return null;
    }, [roiModelsError, detectedModel, modelDisplayName, t]);

    // Local state
    const [activeTab, setActiveTab] = useState<TabKey>('operation');
    const [text, setText] = useState<string>(operation || '');

    // Assistant tab state
    const [query, setQuery] = useState('');
    const [hasQueried, setHasQueried] = useState(false);
    const [isContinueThread, setIsContinueThread] = useState(!DISABLE_CONTINUE_THREAD);
    const [previousQueries, setPreviousQueries] = useState<Array<{ query: string; operation: string }>>([]);
    const [isCurrentOpFlashing, setIsCurrentOpFlashing] = useState(false);
    const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Validate tab state
    const [validateText, setValidateText] = useState('');

    // Ref to capture query value at the time of generation (avoids stale closure in roiOpResult effect)
    const queryRef = useRef(query);
    useEffect(() => { queryRef.current = query; }, [query]);

    // Ref to track last processed result, preventing duplicate history entries (e.g. from React Strict Mode)
    const lastProcessedResultRef = useRef<typeof roiOpResult>(null);

    // Fetch ROI models on mount when assistant tab is enabled
    useEffect(() => {
        if (isOpen && showAssistantTab) {
            dispatch(resetAssistantState());
            dispatch(fetchRoiModelsStarted());
        }
    }, [isOpen, showAssistantTab, dispatch]);

    // Cleanup on close
    useEffect(() => {
        if (!isOpen) {
            dispatch(resetAssistantState());
            setActiveTab('operation');
            setQuery('');
            setHasQueried(false);
            setIsContinueThread(!DISABLE_CONTINUE_THREAD);
            setPreviousQueries([]);
            setValidateText('');
            setIsCurrentOpFlashing(false);
            if (flashTimeoutRef.current) {
                clearTimeout(flashTimeoutRef.current);
                flashTimeoutRef.current = null;
            }
            lastProcessedResultRef.current = null;
        }
    }, [isOpen, dispatch]);

    // When ROI operation is generated successfully, populate Tab 1 and add to history
    useEffect(() => {
        if (roiOpResult && roiOpResult !== lastProcessedResultRef.current) {
            lastProcessedResultRef.current = roiOpResult;
            setText(roiOpResult.operation);
            setPreviousQueries(prev => [...prev, { query: queryRef.current, operation: roiOpResult.operation }]);
            setHasQueried(true);
        }
    }, [roiOpResult]);

    const onConfirmClick = useCallback(() => {
        const parsedText = text.trim();
        onConfirm(parsedText);
        onClose();
    }, [text, onConfirm, onClose]);

    const handleTabSelect = useCallback((_: unknown, key: string | number) => {
        const newTab = key as TabKey;
        // Convenience prefill: seed the Validate tab with the current operation on first
        // visit (while it's still empty). Intentionally one-shot — we don't keep it in sync,
        // since the Validate field is a general, multi-line validator and re-syncing would
        // clobber any extra operations the user typed. Use the "Load current operation"
        // button to deliberately reload the current op.
        if (newTab === 'validate' && !validateText && text) {
            setValidateText(text);
        }
        setActiveTab(newTab);
    }, [validateText, text]);

    const handleTextChange = useCallback((_event: unknown, val: string) => {
        setText(val);
    }, []);

    const handleQueryChange = useCallback((_: unknown, value: string) => {
        setQuery(value);
    }, []);

    const handleGenerateOperation = useCallback(() => {
        if (!detectedModel) { return; }
        const threadId = (!DISABLE_CONTINUE_THREAD && isContinueThread && roiOpThreadId) ? roiOpThreadId : '';
        dispatch(generateRoiOperationStarted({
            query,
            model: detectedModel,
            userThreadId: threadId,
            requestId: uuidv4(),
        }));
    }, [dispatch, query, detectedModel, isContinueThread, roiOpThreadId]);

    const handleValidateTextChange = useCallback((_: unknown, value: string) => {
        setValidateText(value);
    }, []);

    const handleUseCurrentOperation = useCallback(() => {
        setValidateText(text);
    }, [text]);

    const handleValidateOperations = useCallback(() => {
        dispatch(validateRoiOperationsStarted({ operations: validateText }));
    }, [dispatch, validateText]);

    const actions = [
        <Button key="confirm" variant={confirmVariant || 'primary'} onClick={onConfirmClick}>{t('common.ok')}</Button>,
        <Button key="close" variant="tertiary" onClick={onClose}>{t('common.cancel')}</Button>,
    ];

    if (deploymentInfo && deploymentInfo.operationsManualUrl) {
        actions.push(<a
            key="help"
            href={deploymentInfo.operationsManualUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="pf-c-button"
        >
            {t('customizationPage.roiOperations.helpWithAdvancedOperations')}
        </a>);
    }

    const hasTabs = showAssistantTab || showValidateTab;

    const operationTextArea = (
        <div className="roi-operation-tab-content roi-operation-tab-content--operation">
            <TextArea
                className="roi-operation-main-textarea"
                aria-label="text area for an advanced ROI operation"
                rows={4}
                onChange={handleTextChange}
                value={text}
                resizeOrientation="vertical"
            />
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            variant={ModalVariant.medium}
            title={props.roiName ? `${t('common.advancedOperations')} — ${props.roiName}` : t('common.advancedOperations')}
            actions={actions}
            showClose={false}
        >
            {hasTabs ? (
                <Tabs activeKey={activeTab} onSelect={handleTabSelect}>
                    <Tab eventKey="operation" title={<TabTitleText>{t('common.advancedOperations')}</TabTitleText>}>
                        {operationTextArea}
                    </Tab>

                    {showAssistantTab && (
                        <Tab eventKey="assistant" title={<TabTitleText>{t('assistant.roiOp.tabTitle')}</TabTitleText>}>
                            <div className="roi-operation-tab-content" data-testid="roi-op-assistant-tab">
                                <Stack hasGutter>
                                    <StackItem>
                                        <Alert variant="warning" isInline isPlain title={t('assistant.aiWarning')} />
                                    </StackItem>
                                    {areRoiModelsLoading ? (
                                        <StackItem>
                                            <HelperText>
                                                <HelperTextItem>{t('assistant.roiOp.preparingModels')}</HelperTextItem>
                                            </HelperText>
                                        </StackItem>
                                    ) : modelError ? (
                                        <StackItem>
                                            <Alert variant="danger" isInline title={modelError} />
                                        </StackItem>
                                    ) : detectedModel ? (
                                        <StackItem>
                                            <strong>{t('assistant.roiOp.modelLabel')}:</strong>{' '}
                                            {modelDisplayName}
                                        </StackItem>
                                    ) : (
                                        <StackItem>
                                            <HelperText>
                                                <HelperTextItem>{t('assistant.roiOp.noModelDetected')}</HelperTextItem>
                                            </HelperText>
                                        </StackItem>
                                    )}
                                    {!modelError && !areRoiModelsLoading && detectedModel && (<>
                                        {text.trim() && (
                                            <StackItem>
                                                <HelperText>
                                                    <HelperTextItem>
                                                        <strong>{t('assistant.roiOp.currentOperation')}:</strong>{' '}
                                                        <code
                                                            title={text}
                                                            className={isCurrentOpFlashing ? 'roi-operation-current-op-flash' : undefined}
                                                        >
                                                            {text.length > 80 ? text.slice(0, 80) + '...' : text}
                                                        </code>
                                                    </HelperTextItem>
                                                </HelperText>
                                            </StackItem>
                                        )}
                                        <StackItem>
                                            <FormGroup label={t('assistant.roiOp.queryLabel')} fieldId="roi-op-query">
                                                <TextArea
                                                    id="roi-op-query"
                                                    data-testid="roi-op-query"
                                                    value={query}
                                                    onChange={handleQueryChange}
                                                    placeholder={t('assistant.roiOp.queryPlaceholder')}
                                                    rows={3}
                                                    resizeOrientation="vertical"
                                                />
                                                <HelperText>
                                                    <HelperTextItem>{t('assistant.roiOp.queryHelperText')}</HelperTextItem>
                                                </HelperText>
                                            </FormGroup>
                                        </StackItem>
                                        <StackItem>
                                            <Button
                                                variant="primary"
                                                onClick={handleGenerateOperation}
                                                isLoading={isRoiOpGenerating}
                                                isDisabled={isRoiOpGenerating || !query.trim() || !detectedModel}
                                                data-testid="roi-op-generate"
                                            >
                                                {t('assistant.roiOp.generateButton')}
                                            </Button>
                                        </StackItem>
                                        {hasQueried && !DISABLE_CONTINUE_THREAD && (
                                            <StackItem>
                                                <Checkbox
                                                    label={t('assistant.roiOp.continueThread')}
                                                    isChecked={isContinueThread}
                                                    onChange={() => setIsContinueThread(prev => !prev)}
                                                    id="roi-op-continue-thread"
                                                />
                                            </StackItem>
                                        )}
                                        {roiOpError && (
                                            <StackItem>
                                                {roiOpError === 'ASSISTANT_RUN_FAILED' ? (
                                                    <Alert variant="warning" isInline title={t('assistant.roiOp.assistantRunFailed')} />
                                                ) : (
                                                    <Alert variant="danger" isInline title={t('assistant.roiOp.errorTitle')}>
                                                        {roiOpError}
                                                    </Alert>
                                                )}
                                            </StackItem>
                                        )}
                                        {roiOpResult && (
                                            <StackItem>
                                                <Alert variant="success" isInline title={t('assistant.roiOp.successTitle')}>
                                                    <code>{roiOpResult.operation}</code>
                                                    <div>
                                                        <strong>{t('assistant.roiOp.validationStatusLabel')}:</strong> {roiOpResult.validationStatus}
                                                    </div>
                                                </Alert>
                                            </StackItem>
                                        )}
                                        {previousQueries.length > 0 && (
                                            <StackItem>
                                                <ExpandableSection toggleText={t('assistant.roiOp.previousQueries')}>
                                                    {previousQueries.map((pq, i) => (
                                                        <div key={i} className="roi-operation-previous-query">
                                                            <div><strong>Q:</strong> {pq.query}</div>
                                                            <div>
                                                                <strong>A:</strong> <code>{pq.operation}</code>
                                                                {' '}
                                                                <Button
                                                                    variant="link"
                                                                    onClick={() => {
                                                                        setText(pq.operation);
                                                                        setIsCurrentOpFlashing(true);
                                                                        if (flashTimeoutRef.current) {
                                                                            clearTimeout(flashTimeoutRef.current);
                                                                        }
                                                                        flashTimeoutRef.current = setTimeout(() => {
                                                                            setIsCurrentOpFlashing(false);
                                                                            flashTimeoutRef.current = null;
                                                                        }, 600);
                                                                    }}
                                                                >
                                                                    {t('assistant.roiOp.useResult')}
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </ExpandableSection>
                                            </StackItem>
                                        )}
                                    </>)}
                                </Stack>
                            </div>
                        </Tab>
                    )}

                    {showValidateTab && (
                        <Tab eventKey="validate" title={<TabTitleText>{t('assistant.validate.tabTitle')}</TabTitleText>}>
                            <div className="roi-operation-tab-content" data-testid="roi-op-validate-tab">
                                <Stack hasGutter>
                                    <StackItem>
                                        <HelperText>
                                            <HelperTextItem>{t('assistant.validate.description')}</HelperTextItem>
                                        </HelperText>
                                    </StackItem>
                                    {text.trim() && (
                                        <StackItem>
                                            <HelperText>
                                                <HelperTextItem>
                                                    <strong>{t('assistant.roiOp.currentOperation')}:</strong>{' '}
                                                    <code title={text}>
                                                        {text.length > 80 ? text.slice(0, 80) + '...' : text}
                                                    </code>
                                                </HelperTextItem>
                                            </HelperText>
                                        </StackItem>
                                    )}
                                    <StackItem>
                                        <FormGroup label={t('assistant.validate.operationsLabel')} fieldId="roi-op-validate-text">
                                            <TextArea
                                                id="roi-op-validate-text"
                                                data-testid="roi-op-validate-text"
                                                value={validateText}
                                                onChange={handleValidateTextChange}
                                                rows={4}
                                                resizeOrientation="vertical"
                                                placeholder={t('assistant.validate.operationsPlaceholder')}
                                            />
                                            <HelperText>
                                                <HelperTextItem>{t('assistant.validate.operationsHelperText')}</HelperTextItem>
                                            </HelperText>
                                        </FormGroup>
                                    </StackItem>
                                    <StackItem>
                                        <Button
                                            variant="secondary"
                                            onClick={handleUseCurrentOperation}
                                            isDisabled={!text.trim() || validateText === text}
                                            data-testid="roi-op-use-current"
                                        >
                                            {t('assistant.validate.useCurrentButton')}
                                        </Button>
                                        {' '}
                                        <Button
                                            variant="primary"
                                            onClick={handleValidateOperations}
                                            isLoading={isRoiValidating}
                                            isDisabled={isRoiValidating || !validateText.trim()}
                                            data-testid="roi-op-validate"
                                        >
                                            {t('assistant.validate.validateButton')}
                                        </Button>
                                    </StackItem>
                                    {roiValidateError && (
                                        <StackItem>
                                            <Alert variant="danger" isInline title={t('assistant.validate.errorTitle')}>
                                                {roiValidateError}
                                            </Alert>
                                        </StackItem>
                                    )}
                                    {roiValidateResults && (
                                        <StackItem>
                                            <div className="roi-operation-validate-results" data-testid="roi-op-validate-results">
                                                {roiValidateResults.map((r, i) => (
                                                    <div key={i} className={`roi-operation-validate-result ${r.valid ? 'valid' : 'invalid'}`}>
                                                        {r.valid ? <CheckCircleIcon /> : <TimesCircleIcon />}
                                                        <span className="roi-operation-validate-result-text">
                                                            <code>{r.operation}</code>
                                                            {!r.valid && r.error && <span className="roi-operation-validate-error"> — {r.error}</span>}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </StackItem>
                                    )}
                                </Stack>
                            </div>
                        </Tab>
                    )}
                </Tabs>
            ) : (
                operationTextArea
            )}
        </Modal>
    );
}

export default RoiOperationModal;
